import { useState, useEffect } from 'react';
import { assets } from '../../assets/assets.js';
import { NavLink, useNavigate } from 'react-router-dom';
import LanguageSelector from '../LanguageSelector';
import PaymentModal from '../PaymentModal/PaymentModal';
import GlobalSearch from '../GlobalSearch/GlobalSearch';
import ReadingPreferences from '../ReadingPreferences/ReadingPreferences';
import { useAuth } from '../../contexts/AuthContext';

const Navbar = ({ onToggleSidebar, sidebarVisible }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [showGlobalSearch, setShowGlobalSearch] = useState(false);
    const [showReadingPreferences, setShowReadingPreferences] = useState(false);
    const [paymentModal, setPaymentModal] = useState({ isOpen: false, provider: '' });
    const [fontSize, setFontSize] = useState(17);
    const [lineHeight, setLineHeight] = useState(2.1);
    const [fontFamily_state, setFontFamily] = useState('system');
    const [theme, setTheme] = useState('sepia');
    const [readingMode, setReadingMode] = useState(false);

    useEffect(() => {
        const root = document.documentElement;
        let fontFamily = 'Outfit, "Nyala", "Abyssinica SIL", sans-serif';
        switch(fontFamily_state) {
            case 'nyala':       fontFamily = '"Noto Sans Ethiopic", "Nyala", sans-serif'; break;
            case 'abyssinica':  fontFamily = '"Abyssinica SIL", "Noto Sans Ethiopic", sans-serif'; break;
            case 'jiret':       fontFamily = '"Jiret", "Noto Sans Ethiopic", sans-serif'; break;
            case 'visual-geez': fontFamily = '"Visual Geez Unicode", "Noto Sans Ethiopic", sans-serif'; break;
            default: break;
        }
        root.style.setProperty('--app-font-size', `${fontSize}px`);
        root.style.setProperty('--app-line-height', lineHeight);
        root.style.setProperty('--app-font-family', fontFamily);
        document.body.classList.remove('dark-theme', 'sepia-theme', 'high-contrast-theme');
        if (theme === 'dark') document.body.classList.add('dark-theme');
        else if (theme === 'sepia') document.body.classList.add('sepia-theme');
        else if (theme === 'high-contrast') document.body.classList.add('high-contrast-theme');
        if (readingMode) document.body.classList.add('reading-mode');
        else document.body.classList.remove('reading-mode');
        localStorage.setItem('fontPreferences', JSON.stringify({ fontSize, lineHeight, fontFamily: fontFamily_state, theme, readingMode }));
    }, [fontSize, lineHeight, fontFamily_state, theme, readingMode]);

    useEffect(() => {
        const saved = localStorage.getItem('fontPreferences');
        if (saved) {
            const p = JSON.parse(saved);
            setFontSize(p.fontSize || 17);
            setLineHeight(p.lineHeight || 2.1);
            setFontFamily(p.fontFamily || 'system');
            // Migrate old 'light' default to 'sepia'
            const savedTheme = (!p.theme || p.theme === 'light') ? 'sepia' : p.theme;
            setTheme(savedTheme);
            setReadingMode(p.readingMode || false);
        }
    }, []);

    useEffect(() => {
        const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); setShowGlobalSearch(true); } };
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('keydown', onKey); };
    }, []);

    const toggleMenu = () => setMenuOpen(v => !v);
    const closeMenu = () => { setMenuOpen(false); };



    // eslint-disable-next-line no-unused-vars
    const getActiveClass = ({ isActive }) =>
        `px-3 py-1.5 rounded-lg transition-all duration-300 hover:bg-blue-50 hover:text-blue-600 text-sm ${
            isActive ? 'bg-blue-600 text-white hover:bg-blue-700 hover:text-white' : 'text-gray-700'
        }`;

    const iconBtn = 'rounded-lg text-sm flex items-center gap-1 px-2 h-8 transition-all whitespace-nowrap';

    const mobileLinks = [
        ['/bahirehasab', 'ባሕረ ሃሳብ'], ['/sinksar', 'ስንክሳር'], ['/gitsawie', 'ግጻዌ'],
        ['/bible', 'መጽሐፍ ቅዱስ'], ['/wudasie', 'ውዳሴ ማርያም'], ['/seatat', 'ሰአታት'],
        ['/mahlet', 'ሥርዓተ መሕሌት'], ['/filseta', 'እትዓመነኪ'], ['/hibuat', 'ኅቡዓት'],
        ['/books', 'መጻሕፍት'], ['/news', '📰 ዜናዎች'], ['/posting', '📝 ልጥፍ አትም'],
        ['/projects', '🗂️ Projects'],
    ];

    return (
        <>
            <nav style={{background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)'}} className="shadow-sm sticky top-0 z-50">
                <div className="max-w-full px-4 sm:px-6">
                    <div className="flex justify-between items-center h-14">

                        {/* Left: toggle + logo */}
                        <div className="flex items-center gap-1.5 shrink-0">
                            {/* Sidebar toggle — desktop only */}
                            <button
                                onClick={onToggleSidebar}
                                className={`hidden lg:flex flex-col justify-center items-center w-8 h-8 gap-1 rounded-md transition-colors ${sidebarVisible ? 'bg-blue-100' : 'hover:bg-gray-100'}`}
                                aria-label="Toggle sidebar"
                            >
                                <span className={`block w-5 h-0.5 ${sidebarVisible ? 'bg-blue-600' : 'bg-gray-700'}`}></span>
                                <span className={`block w-5 h-0.5 ${sidebarVisible ? 'bg-blue-600' : 'bg-gray-700'}`}></span>
                                <span className={`block w-5 h-0.5 ${sidebarVisible ? 'bg-blue-600' : 'bg-gray-700'}`}></span>
                            </button>

                            {/* Logo */}
                            <NavLink to="/" onClick={closeMenu} className="flex items-center gap-2 hover:opacity-80 transition-opacity shrink-0">
                                <img className="h-9 w-9 object-contain" src={assets.logo} alt="Logo" />
                                <span className="text-base font-bold text-gray-800 hidden sm:block">ግጻዌ፣ ስንክሳር ወመዝገበ ጸሎት</span>
                            </NavLink>
                        </div>

                        {/* Center: desktop nav links — always visible on lg+ */}
                        {/* Right: controls + mobile hamburger */}
                        <div className="flex items-center gap-1">
                            {/* Controls — icon only on mobile, icon+label on lg+ */}
                            <div className="flex items-center gap-1">
                                <div className="rounded-lg">
                                    <LanguageSelector variant="toggle" showFlag={true} showName={false} />
                                </div>
                                <button onClick={() => setShowGlobalSearch(true)} className={`bg-gray-100 hover:bg-gray-200 text-gray-700 ${iconBtn}`} title="Search (Ctrl+K)">
                                    🔍<span className="hidden lg:inline text-xs font-medium ml-0.5">ፈልግ</span>
                                </button>
                                <button onClick={() => setShowReadingPreferences(!showReadingPreferences)} className={`bg-gray-100 hover:bg-gray-200 text-gray-700 ${iconBtn}`} title="Font Settings">
                                    🔤<span className="hidden lg:inline text-xs font-medium ml-0.5">ፊደል</span>
                                </button>
                                <button onClick={() => setTheme(theme === 'dark' ? 'sepia' : 'dark')} className={`bg-gray-100 hover:bg-gray-200 text-gray-700 ${iconBtn}`} title="Toggle Theme">
                                    {theme === 'dark' ? '☀️' : '🌙'}
                                    <span className="hidden lg:inline text-xs font-medium ml-0.5">{theme === 'dark' ? 'ብሩህ' : 'ጨለማ'}</span>
                                </button>


                                {/* Auth button */}
                                {user ? (
                                  <div className="relative group">
                                    <button className="flex items-center gap-1.5 rounded-full overflow-hidden hover:ring-2 hover:ring-blue-400 transition-all">
                                      {user.photoURL
                                        ? <img src={user.photoURL} alt={user.displayName} className="w-8 h-8 rounded-full object-cover" />
                                        : <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">{user.displayName?.[0] || '?'}</div>
                                      }
                                    </button>
                                    <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-100 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                                      <div className="px-4 py-3 border-b border-gray-100">
                                        <p className="text-sm font-semibold text-gray-800 truncate">{user.displayName}</p>
                                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                        {user.isAdmin && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Admin</span>}
                                      </div>
                                      {user.isAdmin && (
                                        <button onClick={() => { navigate('/admin'); closeMenu(); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                                          📊 Analytics
                                        </button>
                                      )}
                                      <button onClick={() => { navigate('/bookmarks'); closeMenu(); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                                        🔖 ምርጦቼ
                                      </button>
                                      <button onClick={logout} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-b-lg">
                                        🚪 ውጣ
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <button onClick={() => navigate('/login')} className={`bg-blue-600 hover:bg-blue-700 text-white ${iconBtn}`}>
                                    👤<span className="hidden lg:inline text-xs font-medium ml-0.5">ግባ</span>
                                  </button>
                                )}
                            </div>

                            {/* Hamburger — always visible, opens nav drawer */}
                            <button onClick={toggleMenu} className="flex flex-col justify-center items-center w-9 h-9 gap-1.5 focus:outline-none ml-1" aria-label="Toggle menu">
                                <span className={`block w-5 h-0.5 bg-gray-800 transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                                <span className={`block w-5 h-0.5 bg-gray-800 transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`}></span>
                                <span className={`block w-5 h-0.5 bg-gray-800 transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Overlay */}
                <div className={`fixed inset-0 bg-gray-900 bg-opacity-50 z-40 transition-opacity duration-300 ${menuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`} onClick={closeMenu}></div>

                {/* Nav drawer — works on all screen sizes */}
                <div className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 overflow-y-auto ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-8">
                            <h2 className="text-xl font-bold text-gray-800">ማውጫ</h2>
                            <button onClick={closeMenu} className="text-gray-600 hover:text-gray-800">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="space-y-2">
                            {mobileLinks.map(([path, label]) => (
                                <NavLink key={path} to={path} onClick={closeMenu} className={({ isActive }) => `block px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
                                    {label}
                                </NavLink>
                            ))}
                        </div>
                    </div>
                </div>
            </nav>

            <PaymentModal isOpen={paymentModal.isOpen} onClose={() => setPaymentModal({ isOpen: false, provider: '' })} provider={paymentModal.provider} />
            <GlobalSearch isOpen={showGlobalSearch} onClose={() => setShowGlobalSearch(false)} />
            <ReadingPreferences isOpen={showReadingPreferences} onClose={() => setShowReadingPreferences(false)} />
        </>
    );
};

export default Navbar;
